import { z } from "zod";
import {
  clientStatuses,
  consentStatuses,
  invoiceStatuses,
  jobChecklistStatuses,
  jobStatuses,
  leadConversationUrgencies,
  leadSources,
  leadStatuses,
  marketingContactStatuses,
  mediaAssetGroups,
  mediaAssetStatuses,
  messageTemplateKeys,
  messageTemplateStatuses,
  quoteItemTypes,
  quotePackageStatuses,
  outreachStatuses,
  suggestionConfidenceLevels,
  targetStatuses,
  verticalKinds
} from "./constants";

export const emailSchema = z.string().trim().toLowerCase().email().max(320);
export const otpCodeSchema = z.string().trim().regex(/^[0-9]{6}$/);
export const idSchema = z.string().min(8).max(80);
export const isoDateTimeSchema = z.string().datetime({ offset: true }).optional().nullable();

export const otpStartSchema = z.object({
  email: emailSchema
});

export const otpVerifySchema = z.object({
  email: emailSchema,
  code: otpCodeSchema,
  name: z.string().trim().min(1).max(120).optional()
});

export const orgUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  vertical: z.enum(verticalKinds).optional(),
  phone: z.string().trim().max(40).optional().nullable(),
  reviewLink: z.string().trim().url().max(500).optional().nullable()
}).refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const profileUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120)
});

export const clientCreateSchema = z.object({
  name: z.string().trim().min(1).max(140),
  email: emailSchema.optional().nullable(),
  phone: z.string().trim().max(40).optional().nullable(),
  notes: z.string().trim().max(2000).optional().nullable(),
  status: z.enum(clientStatuses).default("active")
});

export const clientUpdateSchema = clientCreateSchema.partial().refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const serviceTargetCreateSchema = z.object({
  clientId: idSchema,
  type: z.enum(verticalKinds).default("auto_detailing"),
  label: z.string().trim().min(1).max(160),
  address: z.string().trim().max(500).optional().nullable(),
  details: z.record(z.string(), z.unknown()).default({}),
  status: z.enum(targetStatuses).default("active")
});

export const serviceTargetUpdateSchema = serviceTargetCreateSchema.partial().refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

const leadFieldsSchema = z.object({
  clientId: idSchema.optional().nullable(),
  targetId: idSchema.optional().nullable(),
  source: z.enum(leadSources).default("manual"),
  sourceDetail: z.string().trim().max(160).optional().nullable(),
  summary: z.string().trim().min(1).max(2000),
  conversationText: z.string().trim().max(8000).optional().nullable(),
  status: z.enum(leadStatuses).default("new"),
  quotedCents: z.number().int().nonnegative().optional().nullable(),
  scheduledAt: isoDateTimeSchema
});

export const leadCreateSchema = leadFieldsSchema.superRefine((value, context) => {
  if (value.source === "other" && !value.sourceDetail?.trim()) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sourceDetail"],
      message: "Add a source detail when the lead source is other."
    });
  }
});

export const leadUpdateSchema = leadFieldsSchema.partial().superRefine((value, context) => {
  if (value.source === "other" && !value.sourceDetail?.trim()) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sourceDetail"],
      message: "Add a source detail when the lead source is other."
    });
  }
}).refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const leadConversationAnalysisSchema = z.object({
  summary: z.string().trim().min(1).max(2000).nullable(),
  clientName: z.string().trim().min(1).max(140).nullable(),
  clientPhone: z.string().trim().min(1).max(40).nullable(),
  clientEmail: emailSchema.nullable(),
  vehicleLabel: z.string().trim().min(1).max(160).nullable(),
  serviceAddress: z.string().trim().min(1).max(500).nullable(),
  requestedServices: z.array(z.string().trim().min(1).max(160)).max(8),
  packageHint: z.string().trim().min(1).max(180).nullable(),
  scheduledAt: z.string().datetime({ offset: true }).nullable(),
  scheduleNote: z.string().trim().min(1).max(240).nullable(),
  urgency: z.enum(leadConversationUrgencies).nullable(),
  confidence: z.enum(suggestionConfidenceLevels),
  notes: z.array(z.string().trim().min(1).max(240)).max(8)
});

export const jobCreateSchema = z.object({
  clientId: idSchema.optional().nullable(),
  targetId: idSchema.optional().nullable(),
  leadId: idSchema.optional().nullable(),
  title: z.string().trim().min(1).max(180),
  status: z.enum(jobStatuses).default("draft"),
  scheduledStart: isoDateTimeSchema,
  scheduledEnd: isoDateTimeSchema,
  priceCents: z.number().int().nonnegative().optional().nullable(),
  notes: z.string().trim().max(4000).optional().nullable()
});

export const jobUpdateSchema = jobCreateSchema.partial().refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const jobChecklistItemCreateSchema = z.object({
  label: z.string().trim().min(1).max(180),
  notes: z.string().trim().max(2000).optional().nullable(),
  sortOrder: z.number().int().nonnegative().default(0),
  status: z.enum(jobChecklistStatuses).default("pending")
});

export const jobChecklistItemUpdateSchema = z.object({
  label: z.string().trim().min(1).max(180).optional(),
  notes: z.string().trim().max(2000).optional().nullable(),
  sortOrder: z.number().int().nonnegative().optional(),
  status: z.enum(jobChecklistStatuses).optional(),
  completedAt: isoDateTimeSchema
}).refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const invoiceCreateSchema = z.object({
  amountCents: z.number().int().positive(),
  externalPaymentRef: z.string().trim().max(180).optional().nullable()
});

export const invoiceUpdateSchema = z.object({
  amountCents: z.number().int().positive().optional(),
  status: z.enum(invoiceStatuses).optional(),
  externalPaymentRef: z.string().trim().max(180).optional().nullable(),
  sentAt: isoDateTimeSchema,
  paidAt: isoDateTimeSchema
}).refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const messageTemplateUpdateSchema = z.object({
  title: z.string().trim().min(1).max(120).optional(),
  body: z.string().trim().min(1).max(2000).optional(),
  status: z.enum(messageTemplateStatuses).optional(),
  sortOrder: z.number().int().nonnegative().optional()
}).refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const messageTemplateCreateSchema = z.object({
  templateKey: z.enum(messageTemplateKeys),
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(2000),
  status: z.enum(messageTemplateStatuses).default("active"),
  sortOrder: z.number().int().nonnegative().default(0)
});

export const quotePackageCreateSchema = z.object({
  name: z.string().trim().min(1).max(180),
  description: z.string().trim().max(1000).optional().nullable(),
  basePriceCents: z.number().int().nonnegative(),
  sortOrder: z.number().int().nonnegative().default(0),
  status: z.enum(quotePackageStatuses).default("active")
});

export const quotePackageUpdateSchema = quotePackageCreateSchema.partial().refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const quoteItemInputSchema = z.object({
  name: z.string().trim().min(1).max(180),
  itemType: z.enum(quoteItemTypes).default("addon"),
  quantity: z.number().int().positive().default(1),
  unitPriceCents: z.number().int().nonnegative(),
  sortOrder: z.number().int().nonnegative().default(0)
});

export const quoteDraftCreateSchema = z.object({
  packageId: idSchema,
  title: z.string().trim().min(1).max(180).optional().nullable(),
  notes: z.string().trim().max(4000).optional().nullable(),
  scheduledStart: isoDateTimeSchema,
  scheduledEnd: isoDateTimeSchema,
  addOns: z.array(quoteItemInputSchema).default([])
});

export const quoteDraftUpdateSchema = quoteDraftCreateSchema.partial().refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const quoteSendSchema = z.object({
  sentAt: isoDateTimeSchema.optional().nullable()
});

export const quoteAcceptSchema = z.object({
  scheduledStart: isoDateTimeSchema,
  scheduledEnd: isoDateTimeSchema,
  jobTitle: z.string().trim().min(1).max(180).optional().nullable(),
  jobNotes: z.string().trim().max(4000).optional().nullable()
});

export const mediaAssetCreateSchema = z.object({
  assetGroup: z.enum(mediaAssetGroups).default("before"),
  label: z.string().trim().max(180).optional().nullable()
});

export const mediaAssetUpdateSchema = z.object({
  assetGroup: z.enum(mediaAssetGroups).optional(),
  label: z.string().trim().max(180).optional().nullable(),
  status: z.enum(mediaAssetStatuses).optional()
}).refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export const marketingContactUpsertSchema = z.object({
  id: idSchema.optional(),
  businessName: z.string().trim().min(1).max(180),
  city: z.string().trim().min(1).max(120),
  geoRing: z.string().trim().min(1).max(120).default("Ring 0 - Gainesville"),
  serviceArea: z.string().trim().max(500).optional().nullable(),
  fitScore: z.string().trim().min(1).max(40).default("Medium"),
  contactName: z.string().trim().max(160).optional().nullable(),
  phone: z.string().trim().max(40).optional().nullable(),
  email: emailSchema.optional().nullable(),
  website: z.string().trim().url().max(500).optional().nullable(),
  contactUrl: z.string().trim().url().max(500).optional().nullable(),
  sourceUrl: z.string().trim().url().max(500),
  sourceType: z.string().trim().min(1).max(80).default("Manual"),
  sourceNotes: z.string().trim().max(2000).optional().nullable(),
  outreachChannel: z.string().trim().max(100).optional().nullable(),
  outreachStatus: z.enum(outreachStatuses).default("not_contacted"),
  consentStatus: z.enum(consentStatuses).default("no_marketing_consent"),
  discoveryPriority: z.string().trim().min(1).max(40).default("Medium"),
  painPointHypotheses: z.string().trim().max(2000).optional().nullable(),
  nextAction: z.string().trim().max(2000).optional().nullable(),
  lastVerified: z.string().trim().max(20).optional().nullable(),
  reviewStatus: z.enum(marketingContactStatuses).default("pending_review")
});

export const marketingContactBulkUpsertSchema = z.object({
  contacts: z.array(marketingContactUpsertSchema).min(1).max(250)
});

export const marketingContactReviewSchema = z.object({
  reviewStatus: z.enum(marketingContactStatuses),
  outreachStatus: z.enum(outreachStatuses).optional(),
  consentStatus: z.enum(consentStatuses).optional(),
  nextAction: z.string().trim().max(2000).optional().nullable()
});

export const marketingContactUpdateSchema = z.object({
  phone: z.string().trim().max(40).optional().nullable(),
  email: emailSchema.optional().nullable()
}).refine((value) => Object.keys(value).length > 0, {
  message: "At least one field is required."
});

export type OtpStartInput = z.infer<typeof otpStartSchema>;
export type OtpVerifyInput = z.infer<typeof otpVerifySchema>;
export type OrgUpdateInput = z.infer<typeof orgUpdateSchema>;
export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;
export type ClientCreateInput = z.infer<typeof clientCreateSchema>;
export type ClientUpdateInput = z.infer<typeof clientUpdateSchema>;
export type ServiceTargetCreateInput = z.infer<typeof serviceTargetCreateSchema>;
export type ServiceTargetUpdateInput = z.infer<typeof serviceTargetUpdateSchema>;
export type LeadCreateInput = z.infer<typeof leadCreateSchema>;
export type LeadUpdateInput = z.infer<typeof leadUpdateSchema>;
export type LeadConversationAnalysisInput = z.infer<typeof leadConversationAnalysisSchema>;
export type JobCreateInput = z.infer<typeof jobCreateSchema>;
export type JobUpdateInput = z.infer<typeof jobUpdateSchema>;
export type JobChecklistItemCreateInput = z.infer<typeof jobChecklistItemCreateSchema>;
export type JobChecklistItemUpdateInput = z.infer<typeof jobChecklistItemUpdateSchema>;
export type InvoiceCreateInput = z.infer<typeof invoiceCreateSchema>;
export type InvoiceUpdateInput = z.infer<typeof invoiceUpdateSchema>;
export type MessageTemplateCreateInput = z.infer<typeof messageTemplateCreateSchema>;
export type MessageTemplateUpdateInput = z.infer<typeof messageTemplateUpdateSchema>;
export type QuotePackageCreateInput = z.infer<typeof quotePackageCreateSchema>;
export type QuotePackageUpdateInput = z.infer<typeof quotePackageUpdateSchema>;
export type QuoteItemInput = z.infer<typeof quoteItemInputSchema>;
export type QuoteDraftCreateInput = z.infer<typeof quoteDraftCreateSchema>;
export type QuoteDraftUpdateInput = z.infer<typeof quoteDraftUpdateSchema>;
export type QuoteSendInput = z.infer<typeof quoteSendSchema>;
export type QuoteAcceptInput = z.infer<typeof quoteAcceptSchema>;
export type MediaAssetCreateInput = z.infer<typeof mediaAssetCreateSchema>;
export type MediaAssetUpdateInput = z.infer<typeof mediaAssetUpdateSchema>;
export type MarketingContactUpsertInput = z.infer<typeof marketingContactUpsertSchema>;
export type MarketingContactBulkUpsertInput = z.infer<typeof marketingContactBulkUpsertSchema>;
export type MarketingContactReviewInput = z.infer<typeof marketingContactReviewSchema>;
export type MarketingContactUpdateInput = z.infer<typeof marketingContactUpdateSchema>;
